// Metadata describing each workflow step type the visual editor supports.
// Keep this in sync with backend handlers in app/app/workflows/steps/.

import {
  MdAccessTime,
  MdAdjust,
  MdAlternateEmail,
  MdCallEnd,
  MdCallSplit,
  MdCheck,
  MdCompareArrows,
  MdEditCalendar,
  MdFlag,
  MdHourglassEmpty,
  MdLink,
  MdPhoneForwarded,
  MdSms,
} from "react-icons/md";

export const NODE_TYPES = {
  start: {
    label: "Start",
    icon: MdFlag,
    color: "gray.500",
    category: "Flow",
    outputs: ["default"],
    description: "Entry point — every workflow has one.",
    fields: [],
  },
  end: {
    label: "End",
    icon: MdCheck,
    color: "gray.500",
    category: "Flow",
    outputs: [],
    description: "Terminate the run.",
    fields: [],
  },
  if_time: {
    label: "If Time",
    icon: MdAccessTime,
    color: "purple.500",
    category: "Conditions",
    outputs: ["yes", "no"],
    description: "Branch on current time of day.",
    fields: [
      { name: "operator", label: "Operator", type: "select",
        options: ["between", "outside"], default: "between" },
      { name: "from", label: "From (HH:MM)", type: "text", default: "09:00" },
      { name: "to",   label: "To (HH:MM)",   type: "text", default: "17:00" },
      { name: "days", label: "Days (comma-separated)", type: "text",
        default: "mon,tue,wed,thu,fri",
        parse: (v) => v.split(",").map((s) => s.trim()).filter(Boolean),
        serialize: (v) => Array.isArray(v) ? v.join(",") : "" },
      { name: "timezone", label: "Timezone (IANA)", type: "text",
        default: "America/Los_Angeles" },
      { name: "holidays", label: "Holidays (YYYY-MM-DD, comma-separated)",
        type: "text", default: "",
        parse: (v) => v.split(",").map((s) => s.trim()).filter(Boolean),
        serialize: (v) => Array.isArray(v) ? v.join(",") : "" },
    ],
  },
  set_attribute: {
    label: "Set Variable",
    icon: MdAdjust,
    color: "gray.600",
    category: "Conditions",
    outputs: ["default"],
    description: "Write a value into the run context.",
    fields: [
      { name: "key", label: "Key", type: "text" },
      { name: "value", label: "Value", type: "text" },
    ],
  },
  forward_call: {
    label: "Forward Call",
    icon: MdPhoneForwarded,
    color: "orange.500",
    category: "Call Routing",
    outputs: ["default"],
    description: "Route the inbound call to a phone number (TwiML <Dial>).",
    fields: [
      { name: "number", label: "Forward to (E.164)", type: "text",
        placeholder: "+15551234567" },
    ],
  },
  wait_seconds: {
    label: "Wait (seconds)",
    icon: MdHourglassEmpty,
    color: "yellow.500",
    category: "Timing",
    outputs: ["default"],
    description: "Suspend the run for N seconds.",
    fields: [
      { name: "seconds", label: "Seconds", type: "number", default: 60 },
    ],
  },
  wait_until_event_offset: {
    label: "Wait (event offset)",
    icon: MdHourglassEmpty,
    color: "yellow.600",
    category: "Timing",
    outputs: ["default"],
    description:
      "Resume at payload[event_field] + offset_seconds. Negative = before.",
    fields: [
      { name: "event_field", label: "Event field", type: "text",
        default: "booking_time" },
      { name: "offset_seconds", label: "Offset seconds (negative = before)",
        type: "number", default: -86400 },
    ],
  },
  send_sms: {
    label: "Send SMS",
    icon: MdSms,
    color: "green.500",
    category: "Actions",
    outputs: ["default"],
    description: "Send an SMS via a connected Twilio SMS tool. {{vars}} supported.",
    fields: [
      { name: "link_tool_user_id", label: "Twilio SMS connection (LinkToolUser id)",
        type: "text" },
      { name: "to", label: "To (supports {{vars}})", type: "text",
        placeholder: "{{phone}}" },
      { name: "message", label: "Message (supports {{vars}})", type: "textarea" },
      { name: "from_", label: "From (optional override)", type: "text" },
    ],
  },
  place_call: {
    label: "Place Call",
    icon: MdAlternateEmail,
    color: "blue.500",
    category: "Actions",
    outputs: ["default"],
    description: "Initiate an outbound call via the given voice widget.",
    fields: [
      { name: "widget_id", label: "Voice widget id", type: "text" },
      { name: "to", label: "To (supports {{vars}})", type: "text",
        placeholder: "{{phone}}" },
    ],
  },
  book_pool_slot: {
    label: "Book Pool Slot",
    icon: MdEditCalendar,
    color: "teal.500",
    category: "Actions",
    outputs: ["default"],
    description: "Book on a Calendar Pool member using the pool's strategy.",
    fields: [
      { name: "pool_id", label: "Calendar pool id", type: "text" },
      { name: "appointment_date", label: "Date (YYYY-MM-DD, supports {{vars}})",
        type: "text" },
      { name: "appointment_time", label: "Time (HH:MM, supports {{vars}})",
        type: "text" },
      { name: "email", label: "Email (optional, supports {{vars}})", type: "text" },
      { name: "phone", label: "Phone (optional, supports {{vars}})", type: "text" },
    ],
  },
  call_webhook: {
    label: "Call Webhook",
    icon: MdLink,
    color: "cyan.500",
    category: "Actions",
    outputs: ["default"],
    description: "HTTP POST/PUT/GET to an external URL.",
    fields: [
      { name: "url", label: "URL (supports {{vars}})", type: "text" },
      { name: "method", label: "Method", type: "select",
        options: ["POST", "PUT", "GET"], default: "POST" },
      { name: "body", label: "Body (JSON; supports {{vars}})", type: "json" },
    ],
  },
};

export const CATEGORIES = [
  "Flow",
  "Conditions",
  "Timing",
  "Call Routing",
  "Actions",
];

export function nodeTypesByCategory() {
  const out = {};
  for (const c of CATEGORIES) out[c] = [];
  for (const [key, meta] of Object.entries(NODE_TYPES)) {
    if (!out[meta.category]) out[meta.category] = [];
    out[meta.category].push({ key, ...meta });
  }
  return out;
}

export function defaultNodeData(typeKey) {
  const meta = NODE_TYPES[typeKey];
  if (!meta) return {};
  const data = {};
  for (const f of meta.fields) {
    if (f.default !== undefined) data[f.name] = f.default;
  }
  return data;
}
